import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaCheckDouble } from "react-icons/fa";
import { useU2000NotificationContext } from "../../context/notification/U2000NotificationContext";
import U2000NotificationItem from "./U2000NotificationItem";
import U2000NotificationEmpty from "./U2000NotificationEmpty";
import useU2000LocalStorage from "../../hooks/U2000UseLocalStorage";

interface U2000NotificationPanelProps {
  onClose: () => void;
}

/**
 * Panneau déroulant de notifications
 * Style Material Design avec filtres et actions
 * Optimisé avec gestion du focus pour accessibilité clavier
 */
const U2000NotificationPanel = ({ onClose }: U2000NotificationPanelProps) => {
  const { t } = useTranslation("u2000");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    markAllAsRead,
    loadMore,
  } = useU2000NotificationContext();

  // Filtre "non lues seulement" persisté dans localStorage
  const [showUnreadOnly, setShowUnreadOnly] = useU2000LocalStorage<boolean>("notification_show_unread_only", false);

  // Gérer le focus sur le panneau à l'ouverture
  useEffect(() => {
    // Focus sur le bouton fermer à l'ouverture du panneau
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, []);

  // Gérer le click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && event.target instanceof Node && !panelRef.current.contains(event.target)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Gérer la navigation clavier (Escape pour fermer)
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  // Filtrer les notifications selon le filtre
  const filteredNotifications = showUnreadOnly ? notifications.filter((n) => !n.isRead) : notifications;

  // Gestion du scroll pour charger plus
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (scrollBottom < 100 && hasMore && !isLoading) {
        // Charger plus quand on approche du bas
        loadMore();
      }
    },
    [hasMore, isLoading, loadMore]
  );

  // Gestion du marquage de toutes comme lues
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error("[Notification] Erreur lors du marquage de toutes comme lues:", error);
    }
  };

  return (
    <div
      ref={panelRef}
      className="absolute top-[52px] right-0 z-1000 w-[480px] max-h-[650px] bg-white border border-gray-200 rounded-lg shadow-2xl flex flex-col overflow-hidden"
      role="dialog"
      aria-label={t("lblNotifications")}
      aria-modal="true"
    >
      {/* Header avec gradient */}
      <div className="px-5 py-4 bg-linear-to-r from-blue-600 to-blue-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white" id="notification-panel-title">{t("lblNotifications")}</h2>
          {unreadCount > 0 && (
            <span className="px-2.5 py-0.5 bg-white/20 text-white text-xs font-semibold rounded-full" aria-live="polite">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Bouton marquer toutes comme lues */}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              aria-label={t("lblMarkAllAsRead")}
              title={t("lblMarkAllAsRead")}
            >
              <FaCheckDouble className="w-4 h-4" />
            </button>
          )}
          {/* Bouton fermer */}
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
            aria-label={t("btnClose") || "Fermer"}
            title={t("btnClose") || "Fermer"}
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Toggle filtre */}
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
        <label className="flex items-center gap-2 cursor-pointer group">
          <input
            type="checkbox"
            checked={showUnreadOnly}
            onChange={(e) => setShowUnreadOnly(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
            aria-label={t("lblShowUnreadOnly")}
          />
          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
            {t("lblShowUnreadOnly")}
          </span>
        </label>
      </div>

      {/* Liste des notifications */}
      <div
        className="flex-1 overflow-y-auto focus:outline-hidden"
        onScroll={handleScroll}
        style={{ maxHeight: "calc(650px - 120px)" }}
        role="feed"
        aria-busy={isLoading}
        aria-labelledby="notification-panel-title"
        tabIndex={0}
      >
        {isLoading && filteredNotifications.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <U2000NotificationEmpty />
        ) : (
          <>
            {filteredNotifications.map((notification) => (
              <U2000NotificationItem key={notification.id} notification={notification} />
            ))}
            {isLoading && filteredNotifications.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default U2000NotificationPanel;
